import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext.jsx'
import Card from '../../components/Card.jsx'
import { formatDate } from '../../utils/dateFormat.js'

const BASE = import.meta.env.VITE_API_URL || ''

export default function AccountSettings({ onNavigate }) {
  const { user, updateAccount, deleteAccount, logout } = useAuth()

  const [nameVal,    setNameVal]    = useState(user?.displayName || '')
  const [nameBusy,   setNameBusy]   = useState(false)
  const [nameMsg,    setNameMsg]    = useState('')

  const [curPass,    setCurPass]    = useState('')
  const [newPass,    setNewPass]    = useState('')
  const [newPass2,   setNewPass2]   = useState('')
  const [passBusy,   setPassBusy]   = useState(false)
  const [passMsg,    setPassMsg]    = useState('')

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteBusy,  setInviteBusy] = useState(false)
  const [inviteMsg,   setInviteMsg]  = useState('')
  const [invitations, setInvitations] = useState([])

  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteBusy,    setDeleteBusy]    = useState(false)
  const [deleteErr,     setDeleteErr]     = useState('')

  const [addPassVal,    setAddPassVal]    = useState('')
  const [addPassVal2,   setAddPassVal2]   = useState('')
  const [addPassBusy,   setAddPassBusy]   = useState(false)
  const [addPassMsg,    setAddPassMsg]    = useState('')

  useEffect(() => {
    if (user?.role === 'owner') {
      fetch(`${BASE}/api/auth/invitations`, { credentials: 'include' })
        .then(r => r.json())
        .then(setInvitations)
        .catch(() => {})
    }
  }, [user])

  async function handleNameSave(e) {
    e.preventDefault(); setNameBusy(true); setNameMsg('')
    try {
      await updateAccount({ displayName: nameVal })
      setNameMsg('Name updated.')
    } catch (err) {
      setNameMsg(err.message)
    } finally { setNameBusy(false) }
  }

  async function handlePasswordSave(e) {
    e.preventDefault(); setPassBusy(true); setPassMsg('')
    if (newPass !== newPass2) { setPassMsg('New passwords do not match'); setPassBusy(false); return }
    try {
      await updateAccount({ currentPassword: curPass, newPassword: newPass })
      setPassMsg('Password updated.')
      setCurPass(''); setNewPass(''); setNewPass2('')
    } catch (err) {
      setPassMsg(err.message)
    } finally { setPassBusy(false) }
  }

  async function handleInvite(e) {
    e.preventDefault(); setInviteBusy(true); setInviteMsg('')
    try {
      const res  = await fetch(`${BASE}/api/auth/invite`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed')
      setInviteMsg(`Invitation created for ${inviteEmail}.`)
      setInviteEmail('')
      setInvitations(prev => [data, ...prev])
    } catch (err) {
      setInviteMsg(err.message)
    } finally { setInviteBusy(false) }
  }

  async function handleSetPassword(e) {
    e.preventDefault(); setAddPassBusy(true); setAddPassMsg('')
    if (addPassVal !== addPassVal2) { setAddPassMsg('Passwords do not match'); setAddPassBusy(false); return }
    try {
      const res  = await fetch(`${BASE}/api/auth/set-password`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: addPassVal }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to set password')
      setAddPassMsg('Password set. You can now sign in with email and password.')
      setAddPassVal(''); setAddPassVal2('')
    } catch (err) {
      setAddPassMsg(err.message)
    } finally { setAddPassBusy(false) }
  }

  async function handleDelete(e) {
    e.preventDefault(); setDeleteErr(''); setDeleteBusy(true)
    if (deleteConfirm !== 'DELETE') { setDeleteErr('Type DELETE to confirm'); setDeleteBusy(false); return }
    try {
      await deleteAccount()
    } catch (err) {
      setDeleteErr(err.message)
      setDeleteBusy(false)
    }
  }

  const msgStyle = (txt) => ({
    fontSize: '0.8rem', marginTop: '0.5rem',
    color: txt.includes('updated') || txt.includes('created') ? 'var(--bark)' : '#c0392b',
  })

  return (
    <div className="page active">
      <div className="page-inner">
        <div className="hero">
          <p className="hero-eyebrow">Account</p>
          <h1>Settings</h1>
          <hr className="hero-rule" />
        </div>

        <div style={{ marginBottom: '0.5rem' }}>
          <button className="ghost-btn" onClick={() => onNavigate('daily-summary')}>← Back</button>
        </div>

        {/* Profile info */}
        <Card label="Account" title="Your profile">
          <p style={{ fontSize: '0.83rem', color: 'var(--stone)', marginBottom: '1rem' }}>
            {user?.email} &nbsp;·&nbsp;
            <span style={{ textTransform: 'capitalize', color: 'var(--accent)' }}>{user?.role}</span>
          </p>
          <form onSubmit={handleNameSave}>
            <label className="field-label">Display name</label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <input className="field-input" style={{ flex: 1 }}
                value={nameVal} onChange={e => setNameVal(e.target.value)} required />
              <button className="save-btn ready" type="submit" disabled={nameBusy}>
                {nameBusy ? 'Saving…' : 'Save'}
              </button>
            </div>
            {nameMsg && <p style={msgStyle(nameMsg)}>{nameMsg}</p>}
          </form>
        </Card>

        {/* Sign in methods */}
        <Card label="Account" title="Sign in methods">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--ink)' }}>Email &amp; password</span>
              <span style={{ color: user?.hasPassword === false ? 'var(--stone)' : 'var(--bark)', fontSize: '0.78rem' }}>
                {user?.hasPassword === false ? 'not set' : 'active'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--ink)' }}>Google account</span>
              <span style={{ color: user?.googleId ? 'var(--bark)' : 'var(--stone)', fontSize: '0.78rem' }}>
                {user?.googleId ? 'connected' : 'not connected'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--ink)' }}>Magic link</span>
              <span style={{ color: 'var(--stone)', fontSize: '0.78rem' }}>available</span>
            </div>
          </div>
        </Card>

        {/* Set password — for Google-only users */}
        {user?.googleId && user?.hasPassword === false && (
          <Card label="Account" title="Add a password">
            <p style={{ fontSize: '0.83rem', color: 'var(--stone)', marginBottom: '1rem', lineHeight: 1.6 }}>
              You signed in with Google only. Add a password so you can also sign in with email.
            </p>
            <form onSubmit={handleSetPassword}>
              <div style={{ marginBottom: '0.75rem' }}>
                <label className="field-label">New password</label>
                <input className="field-input" type="password" required minLength={8} autoComplete="new-password"
                  value={addPassVal} onChange={e => setAddPassVal(e.target.value)} placeholder="At least 8 characters" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label className="field-label">Confirm password</label>
                <input className="field-input" type="password" required autoComplete="new-password"
                  value={addPassVal2} onChange={e => setAddPassVal2(e.target.value)} />
              </div>
              <button className="save-btn ready" type="submit" disabled={addPassBusy}>
                {addPassBusy ? 'Saving…' : 'Set password'}
              </button>
              {addPassMsg && <p style={msgStyle(addPassMsg)}>{addPassMsg}</p>}
            </form>
          </Card>
        )}

        {/* Change password — only if account has one */}
        {user?.hasPassword !== false && (
        <Card label="Account" title="Change password">
          <form onSubmit={handlePasswordSave}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label className="field-label">Current password</label>
              <input className="field-input" type="password" required autoComplete="current-password"
                value={curPass} onChange={e => setCurPass(e.target.value)} />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label className="field-label">New password</label>
              <input className="field-input" type="password" required minLength={8} autoComplete="new-password"
                value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="At least 8 characters" />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label className="field-label">Confirm new password</label>
              <input className="field-input" type="password" required autoComplete="new-password"
                value={newPass2} onChange={e => setNewPass2(e.target.value)} />
            </div>
            <button className="save-btn ready" type="submit" disabled={passBusy}>
              {passBusy ? 'Saving…' : 'Change password'}
            </button>
            {passMsg && <p style={msgStyle(passMsg)}>{passMsg}</p>}
          </form>
        </Card>
        )}

        {/* Invitations — owner only */}
        {user?.role === 'owner' && (
          <Card label="Account" title="Invite someone">
            <p style={{ fontSize: '0.83rem', color: 'var(--stone)', marginBottom: '1rem', lineHeight: 1.6 }}>
              Invite someone to create their own account. The link expires in 7 days.
            </p>
            <form onSubmit={handleInvite}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <input className="field-input" type="email" required style={{ flex: 1, minWidth: '200px' }}
                  value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  placeholder="their@email.com" />
                <button className="save-btn ready" type="submit" disabled={inviteBusy}>
                  {inviteBusy ? 'Sending…' : 'Send invite'}
                </button>
              </div>
              {inviteMsg && <p style={msgStyle(inviteMsg)}>{inviteMsg}</p>}
            </form>

            {invitations.length > 0 && (
              <div style={{ marginTop: '1.25rem' }}>
                <p className="field-label" style={{ marginBottom: '0.5rem' }}>Sent invitations</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {invitations.map(inv => (
                    <div key={inv.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      fontSize: '0.8rem', color: 'var(--ink)',
                      padding: '0.4rem 0.6rem', background: 'rgba(0,0,0,0.03)', borderRadius: '6px',
                    }}>
                      <span>{inv.email}</span>
                      <span style={{ color: inv.accepted ? 'var(--bark)' : 'var(--stone)' }}>
                        {inv.accepted ? 'Accepted' : `Expires ${formatDate(inv.expires_at)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Danger zone */}
        <Card label="Account" title="Danger zone">
          <p style={{ fontSize: '0.83rem', color: 'var(--stone)', marginBottom: '1rem', lineHeight: 1.6 }}>
            Permanently delete your account and sign out. This cannot be undone.
          </p>
          <form onSubmit={handleDelete}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label className="field-label">Type <strong>DELETE</strong> to confirm</label>
              <input className="field-input" type="text" value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)} placeholder="DELETE" />
            </div>
            {deleteErr && <p style={{ color: '#c0392b', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{deleteErr}</p>}
            <button
              type="submit" disabled={deleteBusy || deleteConfirm !== 'DELETE'}
              style={{
                background: deleteConfirm === 'DELETE' ? '#c0392b' : 'var(--stone)',
                color: 'white', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem',
                cursor: deleteConfirm === 'DELETE' ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit', fontSize: '0.83rem', opacity: deleteBusy ? 0.7 : 1,
              }}
            >
              {deleteBusy ? 'Deleting…' : 'Delete my account'}
            </button>
          </form>
        </Card>
      </div>
    </div>
  )
}
